import { CaptionedNominalImage } from '@/components/controls';
import { useGroups, useUser, useUserInfo } from '@/hooks';
import { listGroupLabels } from '@/utils';
import { Settings as SettingsIcon } from '@mui/icons-material';
import {
  Box,
  Card,
  CardActions,
  CardContent,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { addDays, format, startOfDay, subDays } from 'date-fns';
import { useState } from 'react';
import { FullImageDialog } from './FullImageDialog';

export function IntelligenceItem({ item, onSettingsClick }) {
  const [dialogImage, setDialogImage] = useState(null);
  const {
    query: { data: user },
  } = useUser(item.edited.username);
  const {
    query: {
      data: { briefingRole },
    },
  } = useUserInfo();
  const {
    query: { data: groups },
  } = useGroups({ types: ['SECTOR', 'TEAM'] });
  const today = startOfDay(new Date());
  const bgcolor =
    item.expiryDate > subDays(today, 3)
      ? 'success.main'
      : item.expiryDate > addDays(today, 5)
        ? 'warning.main'
        : 'error.main';

  const handleImageClick = (src, alt) => () => {
    setDialogImage({ src, alt });
  };

  const handleDialogClose = () => {
    setDialogImage(null);
  };

  return (
    <Card variant="outlined">
      <Stack>
        <CardContent sx={{ flexGrow: 1 }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{ justifyContent: 'space-between' }}
          >
            <Typography variant="caption" color="textSecondary" gutterBottom>
              {user
                ? `${user?.displayName} - ${format(new Date(item.edited.time), 'dd/MM/yyyy HH:mm')}`
                : '-'}
            </Typography>

            <Typography variant="caption" color="textSecondary" gutterBottom>
              {!!item && !!groups
                ? `${listGroupLabels('Group', item.groupCodes, groups)}`
                : ''}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography variant="h6" gutterBottom>
              {item.title}
            </Typography>
            <Tooltip title="Intelligence ID" arrow placement="right">
              <Typography variant="subtitle2" color="primary" gutterBottom>
                {item.intelligenceId}
              </Typography>
            </Tooltip>
          </Stack>
          <Stack spacing={2}>
            <Typography sx={{ whiteSpace: 'pre-wrap' }}>
              {item.message}
            </Typography>
            {item.nominalIds?.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ overflowX: 'auto' }}>
                {item.nominalIds.map((nominalId) => (
                  <CaptionedNominalImage
                    key={nominalId}
                    nominalId={nominalId}
                  />
                ))}
              </Stack>
            )}
            {item.imageSrcs?.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ overflowX: 'auto' }}>
                {item.imageSrcs.map((src, index) => (
                  <img
                    key={index}
                    src={src}
                    alt={`Related ${index + 1}`}
                    height={320}
                    style={{ borderRadius: 4, cursor: 'pointer' }}
                    onClick={handleImageClick(src, `Related ${index + 1}`)}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        </CardContent>
        <CardActions>
          <Tooltip title="Expires">
            <Stack
              direction="row"
              spacing={0.5}
              sx={{ px: 1, justifyItems: 'center' }}
            >
              <Box
                sx={{
                  bgcolor,
                  borderRadius: 8,
                  width: 16,
                  height: 16,
                }}
              />
              <Typography variant="caption" color="textSecondary">
                {item.expiryDate
                  ? format(new Date(item.expiryDate), 'dd/MM/yyyy')
                  : '-'}
              </Typography>
            </Stack>
          </Tooltip>
          <Box sx={{ flexGrow: 1 }} />
          <Stack direction="row" spacing={0.5} sx={{ justifyItems: 'center' }}>
            {(item.categories ?? []).map((category) => (
              <Typography
                key={category}
                variant="caption"
                color="thextSecondary"
              >
                #{category}
              </Typography>
            ))}
          </Stack>
          {briefingRole === 'ADMIN' && (
            <Tooltip title="Settings">
              <IconButton size="small" onClick={onSettingsClick}>
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          )}
        </CardActions>
        <FullImageDialog
          open={!!dialogImage}
          src={dialogImage?.src}
          alt={dialogImage?.alt}
          onClose={handleDialogClose}
        />
      </Stack>
    </Card>
  );
}
